// ─────────────────────────────────────────────────────────────────────────────
// Minimal authed POST /actions helper for the content-script realm.
//
// The `/actions` endpoint takes `{ cmd, ...payload }` and needs the user's
// bearer token. The auth-header/base-url logic here intentionally mirrors
// ai-linked-chats-registry (same lazy-store-require pattern) rather than
// importing from it — keeping this file dependency-free avoids widening that
// module's public surface and sidesteps any module-init cycle (a static
// `import store` from extension-store closes a boot-time cycle; the lazy require
// defers store access to CALL time). The small duplication is deliberate.
// ─────────────────────────────────────────────────────────────────────────────

import { appConfig } from '@common/config/app.config';

function getExtensionStore(): { getState(): any } | null {
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    return require('@extension-context-common/store/extension-store').default ?? null;
  } catch (err) {
    console.warn('[actions-fetch] extension store unavailable', err);
    return null;
  }
}

function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  try {
    const state: any = getExtensionStore()?.getState();
    const accessToken = state?.UserReducer?.accessToken;
    const socketId = state?.UserReducer?.socketId;
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
    if (socketId) headers['Socket'] = socketId;
  } catch (err) {
    console.warn('[actions-fetch] could not read auth headers', err);
  }
  return headers;
}

/**
 * POST a command to the `/actions` endpoint with auth headers and return the
 * parsed JSON body. Throws on a non-2xx response (with the server's error
 * message when available) so callers can render a failure state.
 */
export async function postAction<T = any>(
  cmd: string,
  payload: Record<string, unknown> = {},
): Promise<T> {
  const res = await fetch(`${appConfig.API_ENDPOINT}/actions`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ cmd, ...payload }),
  });
  if (!res.ok) {
    let detail = `Request failed (${res.status})`;
    try {
      const errBody = await res.json();
      detail = errBody?.error || errBody?.message || detail;
    } catch {
      /* keep status-based message */
    }
    throw new Error(detail);
  }
  return res.json() as Promise<T>;
}
